import { formatCents, formatDate } from '@src/common';
import { CartItem, ProductPackage, SaleItem } from '@src/core/buyerApiClient';
import useTranslate from '@src/i18n/useTranslate';
import React, { useMemo } from 'react';

import { CustomAccordion } from '@planne-software/uni/Accordion';
import CartPackageItemDetail from './CartPackageItemDetail';
import {
  PackageDates,
  PackageDiscountValue,
  PackageOriginalPrice,
  PackagePriceInfo,
  PackagePrices,
  PackageProductDetails,
  PackageProductDetailsContent,
  PackageProductWrapper,
  PackageTariffInfo,
  PackageTotalPrice,
  Product,
  ProductName,
} from './styles';
import { calculatePackageTotal } from '@src/domains/cart';
import { getFormattedTariffs } from '@src/features/orders/core/getFormattedTariffs';
import { getPackageDates } from '@src/features/orders/core/getPackageDates';

interface CartPackageItemGroupProps {
  productPackage: ProductPackage;
  items: CartItem[];
}

const CartPackageItemGroup = (props: CartPackageItemGroupProps) => {
  const { productPackage, items } = props;

  const { i18n, locale } = useTranslate();

  const packageDates = useMemo(() => getPackageDates(items, locale), [items, locale]);
  const formattedTariffs = useMemo(() => getFormattedTariffs(items), [items]);

  const packageDiscountCents = useMemo(() => {
    return calculatePackageTotal(items, (item) => item.costPreview.packageDiscountCents);
  }, [items]);

  const totalPriceCentsPackage = useMemo(() => {
    return calculatePackageTotal(items, (item) => item.costPreview.totalPriceCents);
  }, [items]);

  return (
    <Product key={`${productPackage.name}-${productPackage.id}`}>
      <ProductName>{productPackage.name}</ProductName>

      {packageDates && (
        <PackageDates>
          {i18n('cart_package_date')} {packageDates}
        </PackageDates>
      )}

      <PackageTariffInfo>
        {i18n('cart_package_tariff')} {formattedTariffs}
      </PackageTariffInfo>

      <PackagePrices>
        <PackagePriceInfo>
          {i18n('cart_package_previous_price')}
          <PackageOriginalPrice>{formatCents(totalPriceCentsPackage)}</PackageOriginalPrice>
        </PackagePriceInfo>

        <PackagePriceInfo>
          {i18n('cart_package_discount')}
          <PackageDiscountValue>-{formatCents(packageDiscountCents)}</PackageDiscountValue>
        </PackagePriceInfo>

        <PackagePriceInfo>
          {i18n('cart_package_total')}
          <PackageTotalPrice>{formatCents(totalPriceCentsPackage - packageDiscountCents)}</PackageTotalPrice>
        </PackagePriceInfo>
      </PackagePrices>

      <CustomAccordion
        title={<PackageProductDetails>{i18n('cart_package_product_details')}</PackageProductDetails>}
        showBorderBottom
        content={
          <PackageProductDetailsContent>
            <PackageProductWrapper>
              {items.map((item) => (
                <CartPackageItemDetail key={item.id} item={item} />
              ))}
            </PackageProductWrapper>
          </PackageProductDetailsContent>
        }
      />
    </Product>
  );
};

export default CartPackageItemGroup;
